<template>
  <ReportCard :title="$t('report.clients_acquisition_title')">
    <template #header>
      <button @click.stop="showAcquisitionInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.clients_acquisition_info.title')">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>
    <div class="relative">
      <div ref="chartEl" class="w-full h-56"></div>
      <div v-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg transition-opacity">
        <Loading />
      </div>
    </div>
  </ReportCard>

  <BottomSheet v-model:is-open="showAcquisitionInfo" variant="sidebar">
    <ClientsAcquisitionInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import type { SalesByClientsResponse } from '../../responses'
import { computeProjection, trimToPresent } from '../../utils/Projection'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import BottomSheet from '../BottomSheet.vue'
import ClientsAcquisitionInfoSheet from '../ClientsAcquisitionInfoSheet.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: SalesByClientsResponse | null
  seasonalityResponse: SalesByClientsResponse | null
  hasComparison: boolean
  formatXLabel: (dateString: string) => string
  loading: boolean
}>()

const { t } = useI18n()

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()
const showAcquisitionInfo = ref(false)

const isDark = useIsDarkMode()

function buildChartOptions(): echarts.EChartsOption {
  if (!props.response?.intervals) return {}

  const allIntervals = props.response.intervals
  const allNew = allIntervals.map(i => i.data.cumulative.new_clients)
  const cutoff = trimToPresent(allNew)
  const xAxisData = allIntervals.map(i => props.formatXLabel(i.date))
  const totalLen = allIntervals.length

  const dark = isDark.value
  const CYAN = '#06C8FF'
  const newLabel = t('report.clients_new_members_label')

  // Current: sparse array up to cutoff
  const currentFull: (number | null)[] = Array(totalLen).fill(null)
  for (let i = 0; i < cutoff; i++) currentFull[i] = allNew[i]

  const series: echarts.SeriesOption[] = [
    // Current new members line with area
    {
      type: 'line', name: newLabel,
      data: currentFull, smooth: true,
      lineStyle: { width: 3, color: CYAN },
      itemStyle: { color: CYAN },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: dark ? 'rgba(6, 200, 255, 0.12)' : 'rgba(6, 200, 255, 0.10)' },
          { offset: 1, color: 'rgba(6, 200, 255, 0.01)' },
        ]),
      },
      symbol: 'none', showSymbol: false, z: 10,
    },
  ]

  // N-1 comparison line
  if (props.hasComparison && props.response.previous_intervals?.length) {
    const prev = props.response.previous_intervals
    const prevFull: (number | null)[] = Array(totalLen).fill(null)
    const prevLen = Math.min(prev.length, totalLen)
    for (let i = 0; i < prevLen; i++) {
      prevFull[i] = prev[i].data.cumulative.new_clients
    }

    series.push({
      type: 'line', name: newLabel + ' (N-1)',
      data: prevFull, smooth: true,
      lineStyle: { width: 2, color: dark ? 'rgba(255,255,255,0.25)' : 'rgba(100,116,139,0.35)' },
      itemStyle: { color: dark ? 'rgba(255,255,255,0.25)' : 'rgba(100,116,139,0.35)' },
      symbol: 'none', showSymbol: false, z: 6,
    })
  }

  // Projection: Damped Holt + N-1 seasonality blend
  const n1Cumulative = props.seasonalityResponse?.intervals?.length
    ? props.seasonalityResponse.intervals.map(i => i.data.cumulative.new_clients)
    : undefined

  const projResult = computeProjection({
    currentCumulative: allNew,
    n1Cumulative,
  })

  if (projResult) {
    series.push({
      type: 'line',
      name: newLabel + ' (' + t('report.clients_projection').toLowerCase() + ')',
      data: projResult.projected,
      smooth: true,
      lineStyle: { width: 2, color: CYAN, type: [4, 4], opacity: 0.4 },
      itemStyle: { color: CYAN, opacity: 0 },
      symbol: 'none', showSymbol: false,
      connectNulls: false, z: 8,
    })

    const projectedEnd = projResult.projectedEnd
    if (projectedEnd != null) {
      (series[series.length - 1] as any).markPoint = {
        symbol: 'circle', symbolSize: 6,
        itemStyle: { color: CYAN, opacity: 0.6 },
        label: {
          show: true, position: 'right', fontSize: 10, fontWeight: 'bold',
          color: dark ? 'rgba(6, 200, 255, 0.8)' : 'rgba(6, 180, 230, 0.9)',
          formatter: () => '~' + projectedEnd.toLocaleString(),
        },
        data: [{ coord: [totalLen - 1, projectedEnd] }],
      }
    }
  }

  // "Today" mark line
  const effectiveCutoff = projResult?.cutoff ?? cutoff
  if (effectiveCutoff > 0 && effectiveCutoff < totalLen) {
    (series[0] as any).markLine = {
      silent: true, symbol: 'none', animation: false,
      data: [{
        xAxis: effectiveCutoff - 1,
        lineStyle: { color: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)', type: 'solid', width: 1 },
        label: { show: false }, symbol: 'none',
      }],
    }
  }

  return {
    grid: { left: '2%', right: '12%', bottom: '12%', top: '6%', containLabel: true },
    xAxis: {
      type: 'category', data: xAxisData,
      axisLine: { lineStyle: { color: dark ? 'rgba(255,255,255,0.12)' : 'rgba(156,163,175,0.25)' } },
      axisTick: { show: false },
      axisLabel: { color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(75,85,99,0.7)', fontSize: 10 },
    },
    yAxis: {
      type: 'value', minInterval: 1,
      axisLine: { show: false }, axisTick: { show: false }, splitNumber: 4,
      splitLine: { lineStyle: { color: dark ? 'rgba(255,255,255,0.06)' : 'rgba(229,231,235,0.3)', type: 'dashed' } },
      axisLabel: { color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(75,85,99,0.6)', fontSize: 10, formatter: (v: number) => String(Math.round(v)) },
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: dark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.96)',
      borderColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.6)',
      borderWidth: 1, borderRadius: 10, confine: true,
      padding: [8, 12],
      textStyle: { color: dark ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return ''
        let html = `<div style="font-weight:700;margin-bottom:6px;font-size:11px;opacity:0.6">${params[0].axisValue}</div>`
        const current = params.filter((p: any) => p.value != null && !p.seriesName.includes('N-1') && !p.seriesName.includes('projection'))
        const prev = params.filter((p: any) => p.value != null && p.seriesName.includes('N-1'))
        for (const p of current) {
          html += `<div style="display:flex;align-items:center;gap:6px;margin-top:3px">${p.marker}<span style="font-weight:600">${p.seriesName}</span><span style="margin-left:auto;font-weight:700;font-variant-numeric:tabular-nums">${p.value.toLocaleString()}</span></div>`
        }
        if (prev.length) {
          html += `<div style="margin-top:5px;padding-top:5px;border-top:1px solid rgba(128,128,128,0.15)">`
          for (const p of prev) {
            html += `<div style="display:flex;align-items:center;gap:6px;margin-top:2px;opacity:0.5">${p.marker}<span style="font-size:11px">${p.seriesName}</span><span style="margin-left:auto;font-weight:600;font-size:11px;font-variant-numeric:tabular-nums">${p.value.toLocaleString()}</span></div>`
          }
          html += `</div>`
        }
        return html
      },
    },
    dataZoom: [{ type: 'inside', xAxisIndex: [0] }],
    series,
  }
}

function refreshChart() {
  if (chartInstance.value) {
    chartInstance.value.setOption(buildChartOptions(), true)
  } else if (chartEl.value) {
    chartInstance.value = echarts.init(chartEl.value)
    chartInstance.value.setOption(buildChartOptions())
  }
}

watch(() => props.response, async () => {
  await nextTick()
  refreshChart()
})

watch(isDark, () => refreshChart())

watch(() => props.seasonalityResponse, async () => {
  await nextTick()
  refreshChart()
})

watch(chartEl, async (el) => {
  if (el && !chartInstance.value) {
    await nextTick()
    setTimeout(refreshChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

defineExpose({ refreshChart })
</script>
